<script setup>
import { computed } from 'vue';
import MediaImage from '@components/MediaImage.vue';
import { trans, transchoice } from '@elements/translation';
import { number, numberOptionalDecimal, currencyNoDecimal } from '@elements/number-formatter';
import { getShortReadableDate, isDateInFuture } from '@tools/date';
import { useOfficeOfferStore } from '@stores/office-offer';
import OfferTileTag from './OfferTileTag.vue';

const props = defineProps({
  offer: {
    type: Object,
    required: true,
  },
});

const areaUnit = window.AREA_UNIT;
const isOpenSpace = computed(() => useOfficeOfferStore().isOpenSpace(props.offer));
const capacity = computed(() => number(props.offer.capacity));
const isAvailableSoon = computed(() => isDateInFuture(props.offer.availability_date.iso));
const hasFloor = computed(() => props.offer.floor !== null && typeof props.offer.floor === 'number' && Number.isInteger(props.offer.floor));

const getFloor = computed(() => {
  const prefix = 'office_offer.floor.';

  switch (props.offer.floor) {
  case -3:
    return trans(`${prefix}underground`);
  case -2:
    return trans(`${prefix}garden_level`);
  case -1:
    return trans(`${prefix}mezzanine`);
  case 0:
    return trans(`${prefix}ground_level`);
  case 10:
    return trans(`${prefix}ten_more_long_version`);
  default:
    return `${transchoice('global.number.order', { count: props.offer.floor })} ${trans(`${prefix}floor_lowercase`)}`;
  }
});

const tile = computed(() => {
  if (isAvailableSoon.value) {
    return {
      variant: 'status',
      icon: 'available-soon',
      text: transchoice('offer.tags.available_soon', { date: getShortReadableDate(props.offer.availability_date.iso) }),
    };
  }

  return {
    variant: 'status',
    icon: 'available',
    text: trans('offer.tags.available'),
  };
});
</script>

<template>
  <div class="offer-preview" @click="$emit('click')">
    <div class="offer-preview__image">
      <MediaImage
        :uuid="offer.cover.uuid"
        :alt="offer.office_type.name"
        :width="120"
        :height="144"
        :crop="[ 120, 144 ]"
      />
    </div>

    <div class="offer-preview__content">
      <div class="offer-preview-content__data">
        <div class="offer-preview-content-data__title">
          <template v-if="offer.office_type">
            {{ offer.office_type.name }}
          </template>

          <template v-if="hasFloor">
            &bull; {{ getFloor }}
          </template>
        </div>
        <div class="offer-preview-content-data__infos">
          {{ capacity }}
          <template v-if="isOpenSpace">
            {{ transchoice('global.post_office.available', { count: capacity }) }}
          </template>

          <template v-else>
            {{ transchoice('global.post_office', { count: capacity }) }}
            &bull;
            {{ numberOptionalDecimal(offer.surface) }}
            {{ trans('global.area_unit.' + areaUnit) }}
          </template>
        </div>
        <div class="offer-preview-content-data__price">
          {{ currencyNoDecimal(offer.price) }}
          <span v-if="isOpenSpace">{{ trans('offer.per_seat_without_price') }}</span>
        </div>
      </div>

      <div class="offer-preview-content__tag">
        <OfferTileTag
          :variant="tile.variant"
          :start-icon="tile.icon"
        >
          {{ tile.text }}
        </OfferTileTag>
      </div>
    </div>
  </div>
</template>
